import type { ApiError } from '@tiffinify/contracts';

export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';

/** Thin fetch wrapper. The `x-tiffinify` header is the CSRF guard the Worker requires on writes. */
export async function api<T>(method: Method, path: string, body?: unknown, init?: { raw?: BodyInit; contentType?: string }): Promise<T> {
  const headers: Record<string, string> = { 'x-tiffinify': '1' };
  let payload: BodyInit | undefined;
  if (init?.raw) {
    payload = init.raw;
    if (init.contentType) headers['content-type'] = init.contentType;
  } else if (body !== undefined) {
    headers['content-type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { method, headers, body: payload, credentials: 'same-origin' });
  } catch {
    throw new ApiClientError(0, 'network', 'No connection. Check your internet and try again.');
  }
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    // An error page from a proxy (a 502, say), not our API's JSON.
    throw new ApiClientError(res.ok ? 500 : res.status, 'bad_response', 'Something went wrong on our side. Try again in a moment.');
  }
  if (!res.ok) {
    const e = (data as ApiError | null)?.error;
    throw new ApiClientError(res.status, e?.code ?? 'error', e?.message ?? 'Something went wrong', e?.fields);
  }
  return data as T;
}

/** The thing asked for does not exist (or is not this kitchen's), as opposed to a failed request. */
export const isNotFound = (e: unknown) => e instanceof ApiClientError && e.status === 404;

export const get = <T>(path: string) => api<T>('GET', path);
export const post = <T>(path: string, body?: unknown) => api<T>('POST', path, body ?? {});
export const put = <T>(path: string, body?: unknown) => api<T>('PUT', path, body ?? {});
export const del = <T>(path: string) => api<T>('DELETE', path);
